import React, { useRef, useState, useEffect } from 'react';
import { showToaster, toasterTypes } from "..";
import { MESSAGES } from "../../utils/Constants";

interface SignatureCaptureProps {
    onUpload: (fileOrData: string | File) => void;
    label?: string;
    value?: any;
    isPreview?: boolean;
    placeholder?: string;
    maxSize?: number;
    maxLimit?: number;
    acceptFiles?: string;
    messages?: {
        acceptFiles?: string;
        maxSize?: string;
        maxLimit?: string;
    };
    isDisabled?: boolean;
}

interface Point {
    x: number;
    y: number;
}

const SignatureCapture: React.FC<SignatureCaptureProps> = ({
    onUpload,
    label,
    value,
    isPreview,
    placeholder = "Draw your signature below or",
    maxSize,
    maxLimit,
    acceptFiles,
    messages,
    isDisabled = false
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [signature, setSignature] = useState<File | null>(null);
    const [hasDrawn, setHasDrawn] = useState(false);
    const lastPosRef = useRef<Point | null>(null);

    /* ───────── init canvas once ───────── */
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Set canvas styling
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.shadowBlur = 1;
        ctx.shadowColor = 'black';
        ctx.strokeStyle = 'black';
        ctx.lineWidth = 2;

        // Prevent default touch behavior
        const preventDefault = (e: Event) => {
            e.preventDefault();
        };

        canvas.addEventListener('touchstart', preventDefault, { passive: false });
        canvas.addEventListener('touchmove', preventDefault, { passive: false });
        canvas.addEventListener('touchend', preventDefault, { passive: false });

        return () => {
            canvas.removeEventListener('touchstart', preventDefault);
            canvas.removeEventListener('touchmove', preventDefault);
            canvas.removeEventListener('touchend', preventDefault);
        };
    }, []);

    /* ───────── helpers ───────── */
    const getCoordinates = (event: React.TouchEvent | React.MouseEvent | TouchEvent | MouseEvent): Point | null => {
        const canvas = canvasRef.current;
        if (!canvas) return null;

        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;

        if ('touches' in event) {
            const touch = event.touches[0];
            return {
                x: (touch.clientX - rect.left) * scaleX,
                y: (touch.clientY - rect.top) * scaleY
            };
        } else {
            return {
                x: (event.clientX - rect.left) * scaleX,
                y: (event.clientY - rect.top) * scaleY
            };
        }
    };

    const startDrawing = (event: React.TouchEvent | React.MouseEvent) => {
        if (isDisabled) return;
        event.preventDefault();
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        setIsDrawing(true);

        const point = getCoordinates(event);
        if (point) {
            ctx.beginPath();
            ctx.moveTo(point.x, point.y);
            lastPosRef.current = point;
        }
    };

    const draw = (event: React.TouchEvent | React.MouseEvent) => {
        if (isDisabled) return;
        event.preventDefault();
        if (!isDrawing) return;

        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const point = getCoordinates(event);
        if (point && lastPosRef.current) {
            ctx.beginPath();
            ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
            ctx.lineTo(point.x, point.y);
            ctx.stroke();
            lastPosRef.current = point;
            setHasDrawn(true);
        }
    };

    const stopDrawing = (event: React.TouchEvent | React.MouseEvent) => {
        if (isDisabled) return;
        event.preventDefault();
        if (!isDrawing) return;

        setIsDrawing(false);
        lastPosRef.current = null;
    };

    const captureSignature = () => {
        const canvas = canvasRef.current;
        if (!canvas || !hasDrawn) return;

        canvas.toBlob(blob => {
            if (!blob) return;
            validateAndSave(new File([blob], `signature_${Date.now()}.png`, { type: "image/png" }));
        }, "image/png");
    };

    const pickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        if (f) validateAndSave(f);
    };

    const validateAndSave = (file: File) => {
        /* ---------- type guard ---------- */
        const allowed = acceptFiles?.split("|") ?? ["png", "jpg", "jpeg"];
        const ext = file.type.split("/")[1];
        if (!allowed.includes(ext)) {
            let list = allowed.join(", ").replace(/,([^,]*)$/, " and$1");
            showToaster(
                toasterTypes.ERROR,
                messages?.acceptFiles || MESSAGES.ALLOWED_TYPES.replace("#{value}", list)
            );
            return;
        }

        /* ---------- size guard ---------- */
        if (maxSize && file.size > maxSize) {
            showToaster(
                toasterTypes.ERROR,
                messages?.maxSize || MESSAGES.MAX_SIZE.replace("#value", maxSize.toString())
            );
            return;
        }

        /* ---------- total‑size guard ---------- */
        if (maxLimit && file.size > maxLimit) {
            const mb = (maxLimit / (1024 * 1024)).toFixed(2);
            showToaster(
                toasterTypes.ERROR,
                messages?.maxLimit || `Combined file size must not exceed ${mb} MB`
            );
            return;
        }

        setSignature(file);
        onUpload(file);
        showToaster(toasterTypes.SUCCESS, "Signature captured!");
    };

    const clearSignature = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setSignature(null);
        setHasDrawn(false);
    };

    const removeSignature = (e: React.MouseEvent) => {
        setSignature(null);
        setHasDrawn(false);
        clearSignature();
        e.stopPropagation();
    };

    /* ───────── preview modes ───────── */
    if (value && isPreview) {
        return (
            <div className="signature-preview">
                <img src={`https://go.coguide.in/c1/download/${value}`} alt="Signature preview" />
            </div>
        );
    }

    if (!value && isPreview) {
        return (
            <div className="signature-preview">
                <p>No Data</p>
            </div>
        );
    }

    /* ───────── render ───────── */
    return (
        <div className="custom-signature-upload">
            <section className="upload-container">
                {signature ? (
                    <div className="preview-with-delete">
                        <img src={URL.createObjectURL(signature)} alt="signature preview" />
                        <img
                            src={`${process.env.PUBLIC_URL}/images/close_circle.svg`}
                            onClick={removeSignature}
                            alt="remove"
                        />
                    </div>
                ) : (
                    <div className="signature-input-area">
                        <p>
                            <span className="upload-placeholder">{placeholder}</span>
                            <u className="browse-text" onClick={() => fileInputRef.current?.click()}>
                                Browse Image
                            </u>
                        </p>

                        {/* Canvas for drawing */}
                        <div className="signature-canvas-wrapper">
                            <canvas
                                ref={canvasRef}
                                width={400}
                                height={200}
                                className="signature-canvas"
                                style={{ touchAction: 'none' }}
                                onMouseDown={startDrawing}
                                onMouseMove={draw}
                                onMouseUp={stopDrawing}
                                onMouseLeave={stopDrawing}
                                onTouchStart={startDrawing}
                                onTouchMove={draw}
                                onTouchEnd={stopDrawing}
                            />
                            <div className="signature-controls">
                                <button 
                                    type="button"
                                    disabled={isDisabled || !hasDrawn} 
                                    onClick={clearSignature}
                                    className="clear-button"
                                >
                                    Clear
                                </button>
                                <button 
                                    type="button"
                                    disabled={isDisabled || !hasDrawn} 
                                    onClick={captureSignature}
                                    className="capture-button"
                                >
                                    Capture
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Hidden file input for manual pick */}
                <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={pickFile}
                    disabled={isDisabled}
                />
            </section>
        </div>
    );
};

export default SignatureCapture;